import axios from 'axios';

// Determine the API base URL:
// - On custom domains (rotalie.io), use the current origin
// - On preview/development, use the environment variable
export const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = window.location.origin;
  
  // If we're on a custom domain (not emergent preview URLs), use current origin
  if (!currentOrigin.includes('preview.emergentagent.com') && 
      !currentOrigin.includes('localhost') &&
      !currentOrigin.includes('emergent.host')) {
    return currentOrigin;
  }
  
  // Otherwise use environment variable or fallback to current origin
  return envUrl || currentOrigin;
};

export const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Employee API
export const employeeApi = {
  getAll: () => api.get('/employees'),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

// Shift API
export const shiftApi = {
  getAll: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/shifts?${params.toString()}`);
  },
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  copy: (data) => api.post('/shifts/copy', data),
  clear: (data) => api.post('/shifts/clear', data),
  checkConflicts: (data) => api.post('/shifts/check-conflicts', data),
  reassign: (data) => api.post('/shifts/reassign', data),
  bulkDelete: (data) => api.post('/shifts/bulk-delete', data),
};

// Business Hours API
export const businessHoursApi = {
  get: () => api.get('/business-hours'),
  update: (data) => api.put('/business-hours', data),
};

// Break Types API
export const breakTypeApi = {
  getAll: () => api.get('/break-types'),
  create: (data) => api.post('/break-types', data),
  update: (id, data) => api.put(`/break-types/${id}`, data),
  delete: (id) => api.delete(`/break-types/${id}`),
};

// Shift Patterns API
export const shiftPatternApi = {
  getAll: () => api.get('/shift-patterns'),
  create: (data) => api.post('/shift-patterns', data),
  update: (id, data) => api.put(`/shift-patterns/${id}`, data),
  delete: (id) => api.delete(`/shift-patterns/${id}`),
};

// Publish Rota API
export const publishApi = {
  getUnpublishedCount: () => api.get('/shifts/unpublished-count'),
  publish: () => api.post('/shifts/publish'),
  getHistory: (limit = 10) => api.get(`/publish-history?limit=${limit}`),
};

// Absences API
export const absenceApi = {
  getAll: (employeeId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (employeeId) params.append('employee_id', employeeId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString();
    return api.get(`/absences${queryString ? `?${queryString}` : ''}`);
  },
  create: (data) => api.post('/absences', data),
  update: (id, data) => api.put(`/absences/${id}`, data),
  delete: (id) => api.delete(`/absences/${id}`),
  check: (employeeId, date) => api.get(`/absences/check/${employeeId}/${date}`),
};

// Hours Overrides API
export const hoursOverrideApi = {
  getAll: () => api.get('/hours-overrides'),
  create: (data) => api.post('/hours-overrides', data),
  update: (id, data) => api.put(`/hours-overrides/${id}`, data),
  delete: (id) => api.delete(`/hours-overrides/${id}`),
  check: (date) => api.get(`/hours-overrides/check/${date}`),
};

// Subscription Feature Usage API
export const subscriptionApi = {
  getFeatureUsage: () => api.get('/subscription/feature-usage'),
  cleanupFeatureData: (features) => api.post('/subscription/cleanup-feature-data', { features_to_clean: features }),
};

// Events API
export const eventsApi = {
  getColors: () => api.get('/events/colors'),
  getAll: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/events?${params.toString()}`);
  },
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

export default api;
